/**
 * MisPartes — Iter 236.q (Carlos)
 *
 * Página accesible para cualquier usuario: muestra solo los partes diarios
 * que el usuario CREÓ en el mes seleccionado. No incluye partes donde
 * el usuario aparece como personal o supervisor (esos se ven en otras vistas).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import { ClipboardList, CheckCircle2, CircleDashed, Users, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';

function getDefaultMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MES_NOMBRE = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export default function MisPartes() {
  const [mes, setMes] = useState(getDefaultMes);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, mes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/partes-vb/mis-partes', { params: { mes } });
      setData(res.data || { items: [], total: 0 });
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || 'Error al cargar mis partes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => { load(); }, [load]);

  const items = data.items || [];
  const cerrados = items.filter((p) => p.estado === 'cerrado').length;
  const abiertos = items.length - cerrados;
  const conVB = items.filter((p) => p.vb_dado).length;

  // Iter 236.q (Carlos): heatmap del mes — días con partes creados
  const heatmap = useMemo(() => {
    const [year, month] = mes.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    // Construir mapa día→count
    const countByDay = {};
    for (const p of items) {
      const dStr = p.fecha;  // "DD/MM/YYYY"
      if (!dStr) continue;
      const dayPart = parseInt(String(dStr).split('/')[0], 10);
      if (!isNaN(dayPart)) {
        countByDay[dayPart] = (countByDay[dayPart] || 0) + 1;
      }
    }
    // Día de semana del día 1 (0=Dom, 1=Lun, ..., 6=Sab → mapear a L=0)
    const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) {
      const count = countByDay[d] || 0;
      const today = new Date();
      const isToday = today.getFullYear() === year && today.getMonth() + 1 === month && today.getDate() === d;
      const isFuture = new Date(year, month - 1, d) > today;
      cells.push({ day: d, count, isToday, isFuture });
    }
    const max = Math.max(0, ...Object.values(countByDay));
    return { cells, max, year, month, lastDay, countByDay };
  }, [items, mes]);

  const colorForCount = (count, max) => {
    if (count === 0) return 'bg-muted/40 text-muted-foreground';
    if (max <= 1) return 'bg-emerald-300 text-emerald-900';
    const ratio = count / max;
    if (ratio < 0.34) return 'bg-emerald-200 text-emerald-900';
    if (ratio < 0.67) return 'bg-emerald-400 text-emerald-950';
    return 'bg-emerald-600 text-white';
  };

  return (
    <div className="space-y-4 page-enter" data-testid="mis-partes-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Mis Partes Diarios</h1>
          <p className="text-sm text-muted-foreground">Partes que creaste durante el mes</p>
        </div>
        <input
          type="month"
          value={mes}
          onChange={(e) => setMes(e.target.value)}
          className="h-9 rounded-xl border bg-background px-3 text-sm"
          data-testid="input-mes-mis-partes"
        />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-xs text-muted-foreground">Total partes</div>
          <div className="text-xl font-bold tabular-nums">{items.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-muted-foreground">Cerrados</div>
          <div className="text-xl font-bold tabular-nums text-emerald-600">{cerrados}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-muted-foreground">Abiertos</div>
          <div className="text-xl font-bold tabular-nums text-amber-600">{abiertos}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-muted-foreground">Con V°B°</div>
          <div className="text-xl font-bold tabular-nums text-blue-600">{conVB}</div>
        </CardContent></Card>
      </div>

      {/* Iter 236.q (Carlos): Heatmap mensual de partes creados */}
      <Card data-testid="heatmap-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="h-4 w-4" /> Mapa mensual — {MES_NOMBRE[heatmap.month - 1]} {heatmap.year}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 max-w-md">
            {WEEKDAYS.map((wd, i) => (
              <div key={`wd-${i}`} className="text-[10px] text-muted-foreground text-center font-semibold">{wd}</div>
            ))}
            {heatmap.cells.map((cell, idx) => {
              if (!cell) return <div key={`empty-${idx}`} />;
              const cls = cell.isFuture ? 'bg-muted/20 text-muted-foreground/40' : colorForCount(cell.count, heatmap.max);
              const ring = cell.isToday ? 'ring-2 ring-blue-500 ring-offset-1' : '';
              return (
                <div
                  key={`d-${cell.day}`}
                  className={`aspect-square rounded text-[11px] font-medium flex flex-col items-center justify-center transition-colors ${cls} ${ring}`}
                  title={cell.isFuture ? `Día ${cell.day} (futuro)` : `Día ${cell.day}: ${cell.count} parte(s)`}
                  data-testid={`heat-day-${cell.day}`}
                >
                  <div className="text-[10px] opacity-70 leading-tight">{cell.day}</div>
                  {cell.count > 0 && <div className="font-bold leading-tight">{cell.count}</div>}
                </div>
              );
            })}
          </div>
          {/* Leyenda */}
          <div className="flex items-center gap-2 mt-3 text-[10px] text-muted-foreground">
            <span>menos</span>
            <div className="w-3 h-3 rounded bg-muted/40" />
            <div className="w-3 h-3 rounded bg-emerald-200" />
            <div className="w-3 h-3 rounded bg-emerald-400" />
            <div className="w-3 h-3 rounded bg-emerald-600" />
            <span>más</span>
            <span className="ml-3 inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded ring-2 ring-blue-500" /> hoy
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="h-4 w-4" /> Listado · {data.mes || mes}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4"><TableSkeleton /></div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No creaste partes en este mes.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left">Fecha</th>
                    <th className="px-3 py-2 text-left">Supervisor</th>
                    <th className="px-3 py-2 text-left">Lugar / Cliente</th>
                    <th className="px-3 py-2 text-center"># Personas</th>
                    <th className="px-3 py-2 text-center">Estado</th>
                    <th className="px-3 py-2 text-center">V°B°</th>
                    <th className="px-3 py-2 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-muted/20" data-testid={`row-${p.id}`}>
                      <td className="px-3 py-2 font-mono">{p.fecha}</td>
                      <td className="px-3 py-2">{p.supervisor_nombre || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="text-xs">{p.lugar_almuerzo}</div>
                        {p.cliente_nombre && (
                          <div className="text-xs text-muted-foreground truncate max-w-[260px]">{p.cliente_nombre}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center tabular-nums">
                        <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{p.n_personas}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.estado === 'cerrado' ? (
                          <Badge className="bg-emerald-600">cerrado</Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-400 text-amber-700">abierto</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.vb_dado ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 mx-auto" />
                        ) : (
                          <CircleDashed className="h-4 w-4 text-muted-foreground mx-auto" />
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Link to={`/rrhh/parte-diario/${p.id}`} className="text-xs text-blue-600 hover:underline" data-testid={`btn-ver-${p.id}`}>
                          Ver
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
